/**
 * The exact icons the mobile app uses (Ionicons + MDI chef-hat), inlined as
 * SVG so they inherit currentColor. Sourced from ionicons/dist/svg and
 * @mdi/js (both MIT). Filled variants render when `filled` (active tab).
 */

export type AppIconName =
  | 'today'
  | 'chef-hat'
  | 'stats-chart'
  | 'settings'
  | 'add'
  | 'print'
  | 'trash'
  | 'create'
  | 'camera'
  | 'chevron-back'
  | 'chevron-forward'
  | 'flame'
  | 'snow'
  | 'time'
  | 'refresh'
  | 'heart'

const S = 32 // ionicons stroke width (512 viewBox)

export function AppIcon({
  name,
  size = 22,
  filled = false,
  className,
}: {
  name: AppIconName
  size?: number
  filled?: boolean
  className?: string
}) {
  const common = { width: size, height: size, className, 'aria-hidden': true as const }

  if (name === 'chef-hat') {
    return (
      <svg {...common} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.5,1.5C10.73,1.5 9.17,2.67 8.67,4.37C8.14,4.13 7.58,4 7,4A4,4 0 0,0 3,8C3,9.82 4.24,11.41 6,11.87V19H19V11.87C20.76,11.41 22,9.82 22,8A4,4 0 0,0 18,4C17.42,4 16.86,4.13 16.33,4.37C15.83,2.67 14.27,1.5 12.5,1.5M12,10.5H13V17.5H12V10.5M9,12.5H10V17.5H9V12.5M15,12.5H16V17.5H15V12.5M6,20V21A1,1 0 0,0 7,22H18A1,1 0 0,0 19,21V20H6Z" />
      </svg>
    )
  }

  const stroke = {
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeWidth: S,
  }

  switch (name) {
    case 'today':
      return filled ? (
        <svg {...common} viewBox="0 0 512 512" fill="currentColor">
          <path d="M416 64h-16V48.45c0-8.61-6.62-16-15.23-16.43A16 16 0 0 0 368 48v16H144V48.45c0-8.61-6.62-16-15.23-16.43A16 16 0 0 0 112 48v16H96a64 64 0 0 0-64 64v12a4 4 0 0 0 4 4h440a4 4 0 0 0 4-4v-12a64 64 0 0 0-64-64M477 176H35a3 3 0 0 0-3 3v237a64 64 0 0 0 64 64h320a64 64 0 0 0 64-64V179a3 3 0 0 0-3-3M224 307.43A28.57 28.57 0 0 1 195.43 336h-70.86A28.57 28.57 0 0 1 96 307.43v-70.86A28.57 28.57 0 0 1 124.57 208h70.86A28.57 28.57 0 0 1 224 236.57Z" />
        </svg>
      ) : (
        <svg {...common} viewBox="0 0 512 512">
          <rect {...stroke} width="416" height="384" x="48" y="80" rx="48" />
          <path {...stroke} d="M128 48v32M384 48v32" />
          <rect {...stroke} width="96" height="96" x="112" y="224" rx="13" />
          <path {...stroke} d="M464 160H48" />
        </svg>
      )
    case 'stats-chart':
      return filled ? (
        <svg {...common} viewBox="0 0 512 512" fill="currentColor">
          <path d="M104 496H72a24 24 0 0 1-24-24V328a24 24 0 0 1 24-24h32a24 24 0 0 1 24 24v144a24 24 0 0 1-24 24M328 496h-32a24 24 0 0 1-24-24V232a24 24 0 0 1 24-24h32a24 24 0 0 1 24 24v240a24 24 0 0 1-24 24M440 496h-32a24 24 0 0 1-24-24V120a24 24 0 0 1 24-24h32a24 24 0 0 1 24 24v352a24 24 0 0 1-24 24M216 496h-32a24 24 0 0 1-24-24V40a24 24 0 0 1 24-24h32a24 24 0 0 1 24 24v432a24 24 0 0 1-24 24" />
        </svg>
      ) : (
        <svg {...common} viewBox="0 0 512 512">
          <rect {...stroke} width="48" height="160" x="64" y="320" rx="8" />
          <rect {...stroke} width="48" height="256" x="288" y="224" rx="8" />
          <rect {...stroke} width="48" height="368" x="400" y="112" rx="8" />
          <rect {...stroke} width="48" height="448" x="176" y="32" rx="8" />
        </svg>
      )
    case 'settings':
      return filled ? (
        <svg {...common} viewBox="0 0 512 512" fill="currentColor">
          <circle cx="256" cy="256" r="48" />
          <path d="m470.39 300-.47-.38-31.56-24.75a16.11 16.11 0 0 1-6.1-13.33v-11.56a16 16 0 0 1 6.11-13.22L469.92 212l.47-.38a26.68 26.68 0 0 0 5.9-34.06l-42.71-73.9a1.6 1.6 0 0 1-.13-.22A26.86 26.86 0 0 0 401 92.14l-.35.13-37.1 14.93a15.94 15.94 0 0 1-14.47-1.29q-4.92-3.1-10-5.86a15.94 15.94 0 0 1-8.19-11.82l-5.59-39.59-.12-.72A27.22 27.22 0 0 0 298.76 26h-85.52a26.92 26.92 0 0 0-26.45 22.39l-.09.56-5.57 39.67a16 16 0 0 1-8.13 11.82 175 175 0 0 0-10 5.82 15.92 15.92 0 0 1-14.43 1.27l-37.13-15-.35-.14a26.87 26.87 0 0 0-32.48 11.34l-.13.22-42.77 73.95a26.71 26.71 0 0 0 5.9 34.1l.47.38 31.56 24.75a16.11 16.11 0 0 1 6.1 13.33v11.56a16 16 0 0 1-6.11 13.22L42.08 300l-.47.38a26.68 26.68 0 0 0-5.9 34.06l42.71 73.9a1.6 1.6 0 0 1 .13.22 26.86 26.86 0 0 0 32.45 11.3l.35-.13 37.07-14.93a15.94 15.94 0 0 1 14.47 1.29q4.92 3.11 10 5.86a15.94 15.94 0 0 1 8.19 11.82l5.56 39.59.12.72A27.22 27.22 0 0 0 213.24 486h85.52a26.92 26.92 0 0 0 26.45-22.39l.09-.56 5.57-39.67a16 16 0 0 1 8.18-11.82c3.42-1.84 6.76-3.79 10-5.82a15.92 15.92 0 0 1 14.43-1.27l37.13 14.95.35.14a26.85 26.85 0 0 0 32.48-11.34 3 3 0 0 1 .13-.22l42.71-73.89a26.7 26.7 0 0 0-5.89-34.11m-134.48-40.24a80 80 0 1 1-83.66-83.67 80.21 80.21 0 0 1 83.66 83.67" />
        </svg>
      ) : (
        <svg {...common} viewBox="0 0 512 512">
          <path {...stroke} d="M262.29 192.31a64 64 0 1 0 57.4 57.4 64.13 64.13 0 0 0-57.4-57.4M416.39 256a154 154 0 0 1-1.53 20.79l45.21 35.46a10.81 10.81 0 0 1 2.45 13.75l-42.77 74a10.81 10.81 0 0 1-13.14 4.59l-44.9-18.08a16.11 16.11 0 0 0-15.17 1.75A164.5 164.5 0 0 1 325 400.8a15.94 15.94 0 0 0-8.82 12.14l-6.73 47.89a11.08 11.08 0 0 1-10.68 9.17h-85.54a11.11 11.11 0 0 1-10.69-8.87l-6.72-47.82a16.07 16.07 0 0 0-9-12.22 155 155 0 0 1-21.46-12.57 16 16 0 0 0-15.11-1.71l-44.89 18.07a10.81 10.81 0 0 1-13.14-4.58l-42.77-74a10.8 10.8 0 0 1 2.45-13.75l38.21-30a16.05 16.05 0 0 0 6-14.08c-.36-4.17-.58-8.33-.58-12.5s.21-8.27.58-12.35a16 16 0 0 0-6.07-13.94l-38.19-30A10.81 10.81 0 0 1 49.48 186l42.77-74a10.81 10.81 0 0 1 13.14-4.59l44.9 18.08a16.11 16.11 0 0 0 15.17-1.75A164.5 164.5 0 0 1 187 111.2a15.94 15.94 0 0 0 8.82-12.14l6.73-47.89A11.08 11.08 0 0 1 213.23 42h85.54a11.11 11.11 0 0 1 10.69 8.87l6.72 47.82a16.07 16.07 0 0 0 9 12.22 155 155 0 0 1 21.46 12.57 16 16 0 0 0 15.11 1.71l44.89-18.07a10.81 10.81 0 0 1 13.14 4.58l42.77 74a10.8 10.8 0 0 1-2.45 13.75l-38.21 30a16.05 16.05 0 0 0-6.05 14.08c.33 4.14.55 8.3.55 12.47" />
        </svg>
      )
    case 'add':
      return (
        <svg {...common} viewBox="0 0 512 512">
          <path {...stroke} d="M256 112v288M400 256H112" />
        </svg>
      )
    case 'print':
      return (
        <svg {...common} viewBox="0 0 512 512">
          <path {...stroke} d="M384 368h24a40.12 40.12 0 0 0 40-40V168a40.12 40.12 0 0 0-40-40H104a40.12 40.12 0 0 0-40 40v160a40.12 40.12 0 0 0 40 40h24" />
          <rect {...stroke} width="256" height="208" x="128" y="240" rx="24.32" />
          <path {...stroke} d="M384 128v-24a40.12 40.12 0 0 0-40-40H168a40.12 40.12 0 0 0-40 40v24" />
          <circle cx="392" cy="184" r="24" fill="currentColor" />
        </svg>
      )
    case 'trash':
      return (
        <svg {...common} viewBox="0 0 512 512">
          <path {...stroke} d="m112 112 20 320c.95 18.49 14.4 32 32 32h184c17.67 0 30.87-13.51 32-32l20-320" />
          <path {...stroke} d="M80 112h352" />
          <path {...stroke} d="M192 112V72a23.93 23.93 0 0 1 24-24h80a23.93 23.93 0 0 1 24 24v40M256 176v224M184 176l8 224M328 176l-8 224" />
        </svg>
      )
    case 'create':
      return (
        <svg {...common} viewBox="0 0 512 512">
          <path {...stroke} d="M384 224v184a40 40 0 0 1-40 40H104a40 40 0 0 1-40-40V168a40 40 0 0 1 40-40h167.48" />
          <path fill="currentColor" d="M459.94 53.25a16.06 16.06 0 0 0-23.22-.56L424.35 65a8 8 0 0 0 0 11.31l11.34 11.32a8 8 0 0 0 11.34 0l12.06-12c6.1-6.09 6.67-16.01.85-22.38M399.34 90 218.82 270.2a9 9 0 0 0-2.31 3.93L208.16 299a3.91 3.91 0 0 0 4.86 4.86l24.85-8.35a9 9 0 0 0 3.93-2.31L422 112.66a9 9 0 0 0 0-12.66l-9.95-10a9 9 0 0 0-12.71 0" />
        </svg>
      )
    case 'camera':
      return (
        <svg {...common} viewBox="0 0 512 512">
          <path {...stroke} d="m350.54 148.68-26.62-42.06C318.31 100.08 310.62 96 302 96h-92c-8.62 0-16.31 4.08-21.92 10.62l-26.62 42.06C155.85 155.23 148.62 160 140 160H80a32 32 0 0 0-32 32v192a32 32 0 0 0 32 32h352a32 32 0 0 0 32-32V192a32 32 0 0 0-32-32h-59c-8.65 0-16.85-4.77-22.46-11.32" />
          <circle cx="256" cy="272" r="80" fill="none" stroke="currentColor" strokeMiterlimit="10" strokeWidth={S} />
          <path {...stroke} d="M124 158v-22h-24v22" />
        </svg>
      )
    case 'chevron-back':
      return (
        <svg {...common} viewBox="0 0 512 512">
          <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={48} d="M328 112 184 256l144 144" />
        </svg>
      )
    case 'chevron-forward':
      return (
        <svg {...common} viewBox="0 0 512 512">
          <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={48} d="m184 112 144 144-144 144" />
        </svg>
      )
    case 'flame':
      return (
        <svg {...common} viewBox="0 0 512 512" fill="currentColor">
          <path d="M394.23 197.56a300.43 300.43 0 00-53.37-90C301.2 61.65 249.05 32 208 32a16 16 0 00-15.48 20c13.87 53-14.88 97.07-45.31 143.72C122 234.36 96 274.27 96 320c0 88.22 71.78 160 160 160s160-71.78 160-160c0-43.3-7.32-84.49-21.77-122.44zm-105.9 221.13C278 429.69 265.05 432 256 432s-22-2.31-32.33-13.31S208 390.24 208 368c0-25.14 8.82-44.28 17.34-62.78 4.95-10.74 10-21.67 13-33.37a8 8 0 0112.49-4.51A126.48 126.48 0 01275 292c18.17 24 29 52.42 29 76 0 22.24-5.42 39.77-15.67 50.69z" />
        </svg>
      )
    case 'snow':
      // Streak-freeze indicator — ionicons snow-outline, sourced the same way as
      // the other icons in this file (see file header comment).
      return (
        <svg {...common} viewBox="0 0 512 512">
          <path
            {...stroke}
            d="M256 32v448M313.72 80A111.47 111.47 0 0 1 256 96a111.47 111.47 0 0 1-57.72-16M198.28 432a112.11 112.11 0 0 1 115.44 0M449.99 144 62.01 368M437.27 218a112.09 112.09 0 0 1-57.71-100M74.73 294a112.09 112.09 0 0 1 57.71 100M62.01 144l387.98 224M74.73 218a112.09 112.09 0 0 0 57.71-100M437.27 294a112.09 112.09 0 0 0-57.71 100"
          />
        </svg>
      )
    case 'time':
      return (
        <svg {...common} viewBox="0 0 512 512">
          <path {...stroke} d="M256 64C150 64 64 150 64 256s86 192 192 192 192-86 192-192S362 64 256 64Z" />
          <path {...stroke} d="M256 128v144h96" />
        </svg>
      )
    case 'refresh':
      return (
        <svg {...common} viewBox="0 0 512 512">
          <path {...stroke} d="M320 146s24.36-12-64-12a160 160 0 10160 160" />
          <path {...stroke} d="m256 58 80 80-80 80" />
        </svg>
      )
    case 'heart':
      return filled ? (
        <svg {...common} viewBox="0 0 512 512" fill="currentColor">
          <path d="M256 448a32 32 0 0 1-18-5.57c-78.59-53.35-112.62-89.93-131.39-112.8-44.7-54.55-67.29-111.9-67.29-170.42C39.32 96.61 84.72 48 145.68 48c39.36 0 76.26 22.14 96.66 57.44a4.62 4.62 0 0 0 7.32 0C269.72 70.14 306.62 48 346 48h.35c60.94 0 106.34 48.61 106.34 111.21 0 58.52-22.59 115.87-67.29 170.42-18.77 22.87-52.8 59.45-131.39 112.8a32 32 0 0 1-18.01 5.57z" />
        </svg>
      ) : (
        <svg {...common} viewBox="0 0 512 512">
          <path {...stroke} d="M352.92 80C288 80 256 144 256 144s-32-64-96.92-64C106.51 80 64.44 124.14 64 176.81c-.53 62.5 27.79 137.02 233.62 251.16a12 12 0 0 0 8.76 0C512.21 313.83 540.53 239.31 480 176.81 439.56 124.14 397.49 80 352.92 80z" />
        </svg>
      )
  }
}
